import {
  useState, useRef, useEffect, useCallback,
} from 'react';

// UseCases
import {
  getDirectEditType,
  getCellActiveValue,
  getCellDateStringValue,
  shouldSaveValue,
  getSetAttributeUrl,
  validateDirectEditValue,
  convertBooleanToOuiNon,
  isDirectEditDoubleClickType,
} from './tableDirectEditUseCase';

// Services
import { useDirectEditCellValueMutation } from '../../services/directEdit/directEditMutation';

/**
 * @typedef {object} useTableDirectEditReturn
 * @property {boolean} isEditing - Whether the cell is in edit mode
 * @property {string} editValue - Current edited value
 * @property {string | null} validationError - Validation error message (translated)
 * @property {boolean} isPendingSave - Whether the save mutation is pending
 * @property {boolean} hasDirectEdit - Whether direct edit is available
 * @property {string | null} directEditType - The active direct edit type
 * @property {React.RefObject<HTMLDivElement>} containerRef - Ref for the container element
 * @property {(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void} handleBlur - Handle blur event
 * @property {(e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void} handleKeyDown - Handle key down event
 * @property {() => void} handleClick - Handle click to activate edit mode (single-click types)
 * @property {() => void} handleDoubleClick - Handle double click to activate edit mode (double-click-only types)
 * @property {() => void} handleBooleanClick - Handle click for boolean type (toggle and save)
 * @property {(value: string) => void} handleChange - Handle value change
 * @property {(value?: string, cellPatch?: import('../../services/directEdit/directEditCachePatch').DirectEditCellPatch) => void} handleSave - Handle save action
 * @property {() => void} handleCancel - Handle cancel action
 */

/**
 * Hook to manage direct edit functionality for table cells
 * @param {object} props
 * @param {TableCellValueObjectCustom} props.cellValue - Cell value with directEdit config
 * @param {ApiManagerTableColumns} props.column - Column configuration with validation
 * @param {(editing: boolean) => void} [props.onEditingChange] - Called when edit UI opens or closes (e.g. hover suppression)
 * @param {boolean} [props.activateOnMount=false] - Start in edit mode (active cell after a static activate gesture)
 * @returns {useTableDirectEditReturn} Direct edit state and handlers
 */
const useTableDirectEdit = ({
  cellValue,
  column,
  onEditingChange,
  activateOnMount = false,
}) => {
  const directEditType = getDirectEditType(cellValue?.directEdit);
  const startsInEditMode = activateOnMount
    && directEditType !== null
    && directEditType !== 'boolean'
    && directEditType !== 'actionChoice';
  const [isEditing, setIsEditing] = useState(startsInEditMode);
  const [editValue, setEditValue] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const containerRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const blurTimeoutRef = useRef(/** @type {NodeJS.Timeout | null} */ (null));
  const wasEditingRef = useRef(false);
  const didAutoActivateBooleanRef = useRef(false);
  const wasBusyRef = useRef(false);
  const hasDirectEdit = directEditType !== null;
  const cellActiveValue = getCellActiveValue(cellValue);
  const setAttributeUrl = getSetAttributeUrl(cellValue?.directEdit, directEditType);
  const rowId = cellValue?.id;
  const columnId = column?.id;

  const {
    mutate: saveValue,
    isPending: isPendingSave,
  } = useDirectEditCellValueMutation();

  // Single signal to the outside: the cell is "busy" while editing OR saving.
  // CellHoverPreviewWrapper uses this to keep the hover preview suppressed across
  // the editor → pending-save transition (no false `false` between the two states).
  const isBusy = isEditing || isPendingSave;
  useEffect(() => {
    if (isBusy) wasBusyRef.current = true;
    if (!wasBusyRef.current) return;
    onEditingChange?.(isBusy);
  }, [isBusy, onEditingChange]);

  // Mounted without activation (active cell remounted after its editor already opened,
  // e.g. row moved by the refetch after save): release it instead of staying active. Mount only.
  useEffect(() => {
    if (!activateOnMount) onEditingChange?.(false);
  }, []);

  /**
   * Focus the appropriate input element based on directEditType
   * @param {string | null} type - Direct edit type
   */
  const focusInputByType = useCallback((/** @type {string | null} */ type) => {
    if (!containerRef.current || !type) return;

    let input = null;

    switch (type) {
      case 'text':
      case 'color':
      case 'time':
      case 'date':
      case 'datetime':
        input = containerRef.current.querySelector('input');
        break;
      case 'textarea':
        input = containerRef.current.querySelector('textarea');
        break;
      case 'selectClass': {
        const selectControl = /** @type {HTMLElement | null} */ (
          containerRef.current.querySelector('.react-select__control')
        );
        if (selectControl) {
          selectControl.click();
          input = /** @type {HTMLInputElement | null} */ (
            containerRef.current.querySelector('input[id*="react-select"]')
          );
        }
        break;
      }
      default:
        break;
    }

    if (input) {
      input.focus();
      if (type === 'time' && typeof input.select === 'function') {
        input.select();
      }
    }
  }, []);

  // Initialize edit value and focus when entering edit mode (all types)
  useEffect(() => {
    if (isEditing && !wasEditingRef.current) {
      setEditValue(cellActiveValue);
      setValidationError(null);
      setTimeout(() => {
        focusInputByType(directEditType);
      }, 0);
    }
    wasEditingRef.current = isEditing;
  }, [isEditing, cellActiveValue, cellValue, directEditType, focusInputByType]);

  useEffect(() => () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
  }, []);

  /**
   * Handle save - patch cache with submitted value, close edit mode, then POST.
   * @param {string} [valueToSave] - Value to save (defaults to editValue)
   * @param {import('../../services/directEdit/directEditCachePatch').DirectEditCellPatch} [cellPatch] - Optional cell patch (e.g. selectClass label + color)
   */
  const handleSave = useCallback((
    valueToSave = undefined,
    cellPatch = undefined,
  ) => {
    const effectiveValueToSave = valueToSave === undefined ? editValue : valueToSave;

    if (!setAttributeUrl || isPendingSave || !rowId || !columnId) return;

    if (!shouldSaveValue(effectiveValueToSave, cellActiveValue)) {
      setIsEditing(false);
      setValidationError(null);
      return;
    }

    const validation = validateDirectEditValue(effectiveValueToSave, column?.validation);
    if (!validation.isValid) {
      setValidationError(validation.error);
      return;
    }

    setValidationError(null);
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }

    setIsEditing(false);

    saveValue({
      url: setAttributeUrl,
      value: effectiveValueToSave,
      rowId,
      columnId,
      ...(cellPatch !== undefined ? { cellPatch } : {}),
    });
  }, [editValue, cellActiveValue, setAttributeUrl, isPendingSave, column?.validation, rowId, columnId, saveValue, setIsEditing]);

  /**
   * Handle cancel - exit edit mode without saving
   */
  const handleCancel = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setEditValue(cellActiveValue);
    setValidationError(null);
    setIsEditing(false);
  }, [cellActiveValue, setIsEditing]);

  /**
   * Handle click to activate edit mode (types that use single-click activation)
   */
  const handleClick = useCallback(() => {
    if (!hasDirectEdit || isPendingSave) return;
    if (isDirectEditDoubleClickType(directEditType)) return;

    setIsEditing(true);
  }, [hasDirectEdit, directEditType, isPendingSave, setIsEditing]);

  /**
   * Handle double-click to activate edit mode (text, textarea, date, time, etc.)
   */
  const handleDoubleClick = useCallback(() => {
    if (!hasDirectEdit || isPendingSave) return;
    if (!isDirectEditDoubleClickType(directEditType)) return;

    setEditValue(
      directEditType === 'date' ? getCellDateStringValue(cellValue) : cellActiveValue,
    );
    setValidationError(null);
    setIsEditing(true);
  }, [hasDirectEdit, directEditType, isPendingSave, cellActiveValue, cellValue, setIsEditing]);

  /**
   * Handle click for boolean type - toggle value and save immediately.
   */
  const handleBooleanClick = useCallback(() => {
    if (!hasDirectEdit || isPendingSave || directEditType !== 'boolean' || !setAttributeUrl || !rowId || !columnId) return;
    const currentBoolean = /** @type {ApiManagerTableCellBooleanObject} */ (cellActiveValue);
    const newValue = !currentBoolean?.value;
    setEditValue(newValue);

    const stringValue = convertBooleanToOuiNon(newValue);

    saveValue({
      url: setAttributeUrl,
      value: stringValue,
      rowId,
      columnId,
      cellPatch: {
        value: {
          boolean: {
            ...(currentBoolean ?? {}),
            value: newValue,
          },
        },
      },
    });
  }, [hasDirectEdit, isPendingSave, directEditType, cellActiveValue, rowId, columnId, setAttributeUrl, saveValue]);

  useEffect(() => {
    if (!activateOnMount || didAutoActivateBooleanRef.current) return;
    if (directEditType !== 'boolean') return;
    didAutoActivateBooleanRef.current = true;
    handleBooleanClick();
  }, [activateOnMount, directEditType, handleBooleanClick]);

  /**
   * Handle value change
   * @param {string | number} newValue - New value
   */
  const handleChange = useCallback((/** @type {string | number} */ newValue) => {
    setEditValue(String(newValue));
    if (validationError) {
      setValidationError(null);
    }
  }, [validationError]);

  /**
   * Handle key down events
   * @param {React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>} e - Keyboard event
   */
  const handleKeyDown = useCallback((/** @type {React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>} */ e) => {
    // Textarea: Enter inserts a line break; saving happens on blur
    if (e.key === 'Enter' && !e.shiftKey && directEditType !== 'textarea') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  }, [handleSave, handleCancel, directEditType]);

  /**
   * Handle blur - save on blur only if focus moves outside the container
   * @param {React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>} e - Blur event
   */
  const handleBlur = useCallback((/** @type {React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>} */ e) => {
    if (isPendingSave) return;

    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    const isFocusInsideContainer = containerRef.current?.contains(e.relatedTarget);

    if (!isFocusInsideContainer && isEditing && !isPendingSave) {
      blurTimeoutRef.current = setTimeout(() => {
        if (isEditing && !isPendingSave) {
          handleSave();
        }
      }, 150);
    }
  }, [isPendingSave, isEditing, handleSave]);

  return {
    // State
    isEditing,
    editValue,
    validationError,
    isPendingSave,
    hasDirectEdit,
    directEditType,

    // Refs
    containerRef,

    // Handlers
    handleBlur,
    handleKeyDown,
    handleClick,
    handleDoubleClick,
    handleBooleanClick,
    handleChange,
    handleSave,
    handleCancel,
  };
};

export default useTableDirectEdit;
